import { pb } from '@/libs/pocketbase'
import { tryCatch } from '@/utils/try-catch'
import type { CreateWorkItemInput, UpdateWorkItemInput, WorkItem, WorkItemPort } from '../domain/workflow'

// Thin transport over the PocketBase client. Every call disables request
// de-duplication (requestKey: null) so rapid board interactions are never
// silently dropped, and surfaces the backend error message verbatim, which the
// use-cases turn into user-facing toasts.

const query = (params: Record<string, string | null | undefined>) => {
	const search = new URLSearchParams()
	for (const [key, value] of Object.entries(params)) {
		if (value === null || value === undefined || value === '') continue
		search.set(key, value)
	}
	const value = search.toString()
	return value ? `?${value}` : ''
}

const get = async <T>(path: string): Promise<T> => {
	const result = await tryCatch(pb.send<T>(`/api/work-items${path}`, { method: 'GET', requestKey: null }))
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data
}

const send = async <T>(method: 'POST' | 'PATCH', path: string, body?: unknown): Promise<T> => {
	const result = await tryCatch(
		pb.send<T>(`/api/work-items${path}`, {
			method,
			requestKey: null,
			...(body === undefined
				? {}
				: { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
		}),
	)
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data
}

const post = <T>(path: string, body?: unknown) => send<T>('POST', path, body)
const patch = <T>(path: string, body?: unknown) => send<T>('PATCH', path, body)

const pbWorkItems: WorkItemPort = {
	listByWorkflow: (companyWorkflowId, teamId, filter) =>
		get<WorkItem[]>(
			query({
				companyWorkflowId,
				teamId,
				status: filter?.status,
				assigneeId: filter?.assigneeId,
				priority: filter?.priority,
				labels: filter?.labels?.length ? filter.labels.join(',') : undefined,
				completedFrom: filter?.completedFrom,
				completedTo: filter?.completedTo,
			}),
		),
	listByParent: parentId => get<WorkItem[]>(query({ parentId })),
	getById: id => get<WorkItem | null>(`/${id}`),
	create: (input: CreateWorkItemInput) => post<WorkItem>('', input),
	update: ({ id, ...patchBody }: UpdateWorkItemInput) => patch<WorkItem>(`/${id}`, patchBody),
	updateSpawnMode: (id, spawnMode) => patch<WorkItem>(`/${id}/spawn-mode`, { spawnMode }),
	setSpawnLightweight: (id, lightweight, actorId, actorName) =>
		patch<WorkItem>(`/${id}/lightweight`, { lightweight, actorId, actorName }),
	concludeSpawn: (id, status, actorId, actorName) =>
		post<WorkItem>(`/${id}/conclude-spawn`, { status, actorId, actorName }),
	assign: (id, memberId, actorId, actorName) =>
		post<WorkItem>(`/${id}/assign`, { memberId, actorId, actorName }),
	fireTransition: (id, transitionId, actorId, actorName) =>
		post<WorkItem>(`/${id}/transition`, { transitionId, actorId, actorName }),
	moveToStep: (id, stepId, actorId, actorName) =>
		post<WorkItem>(`/${id}/move-to-step`, { stepId, actorId, actorName }),
	cancelWorkItem: (id, actorId, actorName, reason) =>
		post<WorkItem>(`/${id}/cancel`, { actorId, actorName, reason }),
	completeWorkItem: (id, actorId, actorName) =>
		post<WorkItem>(`/${id}/complete`, { actorId, actorName }),
	reopen: (id, actorId, actorName) => post<WorkItem>(`/${id}/reopen`, { actorId, actorName }),
	setWorkState: (id, workState, actorId, actorName) =>
		patch<WorkItem>(`/${id}/work-state`, { workState, actorId, actorName }),
	acceptHandoff: (id, actorId, actorName) => post<WorkItem>(`/${id}/accept`, { actorId, actorName }),
	rejectHandoff: (id, actorId, actorName, reason) =>
		post<WorkItem>(`/${id}/reject`, { actorId, actorName, reason }),
	reassignWorkflow: ({ id, ...body }) => post<WorkItem>(`/${id}/reassign`, body),
	addComment: (id, authorId, authorName, body) =>
		post<WorkItem>(`/${id}/comments`, { authorId, authorName, body }),
	editComment: (id, commentId, body) => patch<WorkItem>(`/${id}/comments/${commentId}`, { body }),
	archive: (id, actorId, actorName, force) =>
		post<WorkItem>(`/${id}/archive`, { actorId, actorName, force }),
}

export { pbWorkItems }
